import { BaseTimePeriod, TimePeriod } from '@services/FiltersTypes';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import {
  getDefaultCustomMonthsPeriod,
  getDefaultCustomWeeksPeriod,
  getPeriodicityKey,
  getPeriodsWithoutYtdMat,
  isPeriodicityMATOrYTD,
  isValidCustomPeriodSelections,
} from '../PeriodUtils';
import { unhandledEnum } from '../CompileTimeSafety';
import { PeriodCombo } from './PeriodSet';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { CustomMonthsPeriod, CustomWeeksPeriod } from './UseInitializedPeriodSet';
import { CustomMonthsPeriodState, CustomWeeksPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';
import { PageName } from '../pageConfig/Types';

/**
 * Returns a new period combo, if the current period combo should be reset.
 * When:
 * 1. Period combo does not need to be reset: returns null.
 * 2. Period combo periodicity is not supported: returns the latest monthly/weekly period combo.
 * 3. Time period option is empty: returns the latest monthly/weekly period combo.
 * 4. Time period option does not contain its period: returns the latest period combo
 * of the same periodicity.
 *
 * @param givenCombo the current period combo
 * @param supportedPeriodicities list of periodicities supported by the page
 * @param timePeriodOptions all available period options, across all available periodicities
 * @param customMonthsPeriodState
 * @param customWeeksPeriodState
 * @param pageName
 * @returns newPeriodCombo if givenCombo should be reset
 * @returns null if givenCombo should be persisted
 */
export const getFallbackPeriodCombo = (
  givenCombo: PeriodCombo,
  supportedPeriodicities: Periodicity[],
  timePeriodOptions: TimePeriod,
  customMonthsPeriodState?: CustomMonthsPeriodState,
  customWeeksPeriodState?: CustomWeeksPeriodState,
  pageName?: PageName,
): PeriodCombo | null => {
  let actualFallbackPeriodicity = getFallbackPeriodicity(givenCombo, supportedPeriodicities, timePeriodOptions);

  if (isPeriodicityMATOrYTD(givenCombo.periodicity)) {
    actualFallbackPeriodicity = givenCombo.periodicity;
  }
  // Remove this code once RO implemented MAT and YTD in Time Frame periodicity
  if (pageName === PageName.RETAILER_OVERVIEW) {
    //Handling for MAT/YTD
    const actualPeriodicity = getActualPeriodicity(givenCombo.period, givenCombo.periodicity);
    if (actualPeriodicity !== givenCombo.periodicity) {
      actualFallbackPeriodicity = givenCombo.periodicity;
    }
  }

  const isFallBackFromCustomMonths =
    givenCombo.periodicity === Periodicity.CustomMonths && givenCombo.periodicity !== actualFallbackPeriodicity;

  const isFallBackFromCustomWeeks =
    givenCombo.periodicity === Periodicity.CustomWeeks && givenCombo.periodicity !== actualFallbackPeriodicity;

  //To check if previous selection is custom months, then need to reset periodicity to Monthly/Weekly, then default
  //comparison period to Previous Year Current Period
  const shouldFallback = isFallBackFromCustomMonths || isFallBackFromCustomWeeks;

  const comparisonPeriod = shouldFallback ? RelativePeriod.PreviousYearCurrentPeriod : givenCombo.comparisonPeriod;

  if (actualFallbackPeriodicity === null) {
    return null;
  }

  const customMonthsDefaultPeriod: CustomMonthsPeriod = getDefaultCustomMonthsPeriod(timePeriodOptions);

  //To check if comparisonPeriodSet from redux is not undefined, and the focus period
  //and comparison period is valid from latest period, then use redux value, else use default
  const customMonthsToBeSet = isValidCustomPeriodSelections(customMonthsPeriodState, timePeriodOptions.customMonths)
    ? (customMonthsPeriodState as CustomMonthsPeriod)
    : customMonthsDefaultPeriod;

  const customWeeksDefaultPeriod: CustomWeeksPeriod = getDefaultCustomWeeksPeriod(timePeriodOptions);

  const customWeeksToBeSet = isValidCustomPeriodSelections(customWeeksPeriodState, timePeriodOptions.customWeeks)
    ? (customWeeksPeriodState as CustomWeeksPeriod)
    : customWeeksDefaultPeriod;

  return getLatestPeriodCombo(
    timePeriodOptions,
    actualFallbackPeriodicity,
    givenCombo.period,
    comparisonPeriod,
    customMonthsToBeSet,
    customWeeksToBeSet,
  );
};

export const shouldResetComparisonPeriod = (givenComparison: RelativePeriod | BaseTimePeriod) => {
  return RelativePeriod.PreviousYearCurrentPeriod !== givenComparison;
};

/**
 * Returns the latest (or only) period combo given a periodicity.
 * Note that stale period is used by YTD/MAT's case only to determine if the previous period is Monthly or Weekly YTD/MAT.
 *
 * @param timePeriodOptions
 * @param periodicity
 * @param stalePeriod
 * @param comparisonPeriod
 * @param customMonthsPeriod
 * @param customWeeksPeriod
 */
export const getLatestPeriodCombo = (
  timePeriodOptions: TimePeriod,
  periodicity: Periodicity,
  stalePeriod: BaseTimePeriod | null,
  comparisonPeriod: RelativePeriod | BaseTimePeriod,
  customMonthsPeriod: CustomMonthsPeriod,
  customWeeksPeriod: CustomWeeksPeriod,
): PeriodCombo => {
  switch (periodicity) {
    case Periodicity.Monthly:
    case Periodicity.Weekly:
    case Periodicity.CalendarQuarter:
    case Periodicity.RollingQuarter: {
      const fallbackPeriod = getPeriodSliceDescendingOrder(timePeriodOptions, periodicity)[0];
      return {
        period: fallbackPeriod,
        periodicity: periodicity,
        comparisonPeriod: comparisonPeriod,
      };
    }
    case Periodicity.CustomMonths: {
      return {
        period: customMonthsPeriod.period,
        periodicity: periodicity,
        comparisonPeriod: customMonthsPeriod.comparisonPeriod,
      };
    }
    case Periodicity.CustomWeeks: {
      return {
        period: customWeeksPeriod.period,
        periodicity: periodicity,
        comparisonPeriod: customWeeksPeriod.comparisonPeriod,
      };
    }
    case Periodicity.MAT:
    case Periodicity.YTD: {
      if (stalePeriod === null) throw new Error('getLatestPeriodCombo: cannot initialize Redux to YTD period');

      // E.g. [YTD Jan 2023 - Dec 2023, YTD Jan 2023 - Nov 2023, YTD Week 1 2023 - Week 52 2023,
      // YTD Week 1 2023 - Week 52 2023]
      const ytdOrMatPeriodOptions = getPeriodSliceDescendingOrder(timePeriodOptions, periodicity);

      let fallbackPeriod = ytdOrMatPeriodOptions[0];
      if (stalePeriod.label.includes('Week')) {
        fallbackPeriod = ytdOrMatPeriodOptions.filter((entry) => entry.label.includes('Week'))[0];
      }

      return {
        period: fallbackPeriod,
        periodicity: getApparentPeriodicity(fallbackPeriod, periodicity),
        comparisonPeriod: comparisonPeriod,
      };
    }
    case Periodicity.Custom: {
      throw new Error('getLatestPeriodCombo: custom period is not implemented!');
    }
    default: {
      unhandledEnum(periodicity);
      // unreachable statement
      return periodicity;
    }
  }
};

/**
 * Returns a periodicity to reset to, if the current period combo should be reset.
 * If the period combo does not need to be reset, returns null.
 * If the period combo needs to be reset, either returns the same periodicity, or a fallback monthly/weekly
 * periodicity depending on conditions.
 */
const getFallbackPeriodicity = (
  givenCombo: PeriodCombo,
  supportedPeriodicities: Periodicity[],
  timePeriodOptions: TimePeriod,
): Periodicity | null => {
  const { period: givenPeriod, periodicity: givenPeriodicity } = givenCombo;

  if (!supportedPeriodicities.includes(givenPeriodicity)) {
    return getMonthlyWeeklyPeriodicity(timePeriodOptions);
  }

  const periodicityKey = getPeriodicityKey(givenPeriodicity) as keyof TimePeriod;
  if (!timePeriodOptions[periodicityKey] || timePeriodOptions[periodicityKey].length === 0) {
    return getMonthlyWeeklyPeriodicity(timePeriodOptions);
  }

  const actualPeriodicity = getActualPeriodicity(givenPeriod, givenPeriodicity);
  const periodSlice = getPeriodSliceDescendingOrder(timePeriodOptions, actualPeriodicity);

  if (!periodSlice.some((period) => period.label === givenPeriod.label)) {
    // Note: have to match by label, as MAT/YTD value is just MAT/YTD with no period value
    return actualPeriodicity;
  }

  return null;
};

const getMonthlyWeeklyPeriodicity = (timePeriodOptions: TimePeriod): Periodicity => {
  if (timePeriodOptions.monthly && timePeriodOptions.monthly.length !== 0) {
    return Periodicity.Monthly;
  } else if (timePeriodOptions.weekly && timePeriodOptions.weekly.length !== 0) {
    return Periodicity.Weekly;
  } else {
    return Periodicity.Weekly; // return fallback periodicity instead of throwing error and caused blank screen
  }
};

/**
 * Actual periodicity means periodicity where YTD/MAT split and are by their own.
 * @param givenPeriod
 * @param givenApparentPeriodicity
 */
const getActualPeriodicity = (givenPeriod: BaseTimePeriod, givenApparentPeriodicity: Periodicity): Periodicity => {
  switch (givenApparentPeriodicity) {
    case Periodicity.CalendarQuarter:
    case Periodicity.RollingQuarter:
    case Periodicity.Custom:
    case Periodicity.CustomMonths:
    case Periodicity.CustomWeeks:
      return givenApparentPeriodicity;
    case Periodicity.Monthly:
    case Periodicity.Weekly: {
      if (givenPeriod.label.includes('MAT')) return Periodicity.MAT;
      if (givenPeriod.label.includes('YTD')) return Periodicity.YTD;
      return givenApparentPeriodicity;
    }
    case Periodicity.YTD:
    case Periodicity.MAT: {
      throw new Error(`getActualPeriodicity: unreachable case for ${givenApparentPeriodicity}`);
    }
    default:
      unhandledEnum(givenApparentPeriodicity);
      // unreachable statement
      return givenApparentPeriodicity;
  }
};

/**
 * Apparent periodicity means periodicity where YTD/MAT are shown as Monthly/Weekly
 */
const getApparentPeriodicity = (
  givenPeriod: BaseTimePeriod,
  givenActualPeriodicity: Periodicity.YTD | Periodicity.MAT,
): Periodicity => {
  switch (givenActualPeriodicity) {
    case Periodicity.YTD:
    case Periodicity.MAT: {
      if (givenPeriod.label.includes('Week')) {
        return Periodicity.Weekly;
      } else {
        return Periodicity.Monthly;
      }
    }
    default:
      unhandledEnum(givenActualPeriodicity);
      // unreachable statement
      return givenActualPeriodicity;
  }
};

/**
 * Returns a slice of periods in descending order
 * 1. For monthly/weekly, create a slice of 5/3 latest periods
 * 2. For YTD/MAT, create a slice out of monthly/weekly, where monthly comes before weekly
 * 3. For other periodicities, same slice is used
 *
 * Assumptions:
 * 1. For Monthly/Weekly, there will always be 1 YTD and 1 MAT at the end
 * 2. For Monthly/Weekly, periods are always in ascending order (latest periods are near the bottom)
 * 3. For Quarters, periods are always in descending order
 */
const getPeriodSliceDescendingOrder = (
  timePeriodOptions: TimePeriod,
  actualPeriodicity: Periodicity,
): BaseTimePeriod[] => {
  switch (actualPeriodicity) {
    case Periodicity.Monthly:
    case Periodicity.Weekly:
      return getMonthlyWeeklySlice(actualPeriodicity, timePeriodOptions);
    case Periodicity.MAT:
    case Periodicity.YTD: {
      return getMatYtdSlice(actualPeriodicity, timePeriodOptions);
    }
    // quarter slices are clean, can be used directly
    case Periodicity.CalendarQuarter:
    case Periodicity.RollingQuarter:
    case Periodicity.CustomMonths:
    case Periodicity.CustomWeeks: {
      const periodicityKey = getPeriodicityKey(actualPeriodicity) as keyof TimePeriod;
      return timePeriodOptions[periodicityKey];
    }
    case Periodicity.Custom: {
      throw new Error(`getPeriodSlice: unhandled case ${actualPeriodicity}`);
    }
    default:
      unhandledEnum(actualPeriodicity);
      // unreachable statement
      return [];
  }
};

const MONTHLY_SIZE = 3;
const WEEKLY_SIZE = 5;

const getMonthlyWeeklySlice = (
  periodicity: Periodicity.Monthly | Periodicity.Weekly,
  timePeriodOptions: TimePeriod,
): BaseTimePeriod[] => {
  const periodicityKey = getPeriodicityKey(periodicity) as keyof TimePeriod;
  const rawSlice: BaseTimePeriod[] = getPeriodsWithoutYtdMat(timePeriodOptions[periodicityKey]);

  if (rawSlice.length === 0) {
    return [
      {
        label: '',
        value: '',
        lastUpdatedDate: 0,
      },
    ];
  }

  switch (periodicity) {
    case Periodicity.Monthly:
      return rawSlice.slice(-MONTHLY_SIZE).reverse();
    case Periodicity.Weekly:
      return rawSlice.slice(-WEEKLY_SIZE).reverse();
  }
};

const getMatYtdSlice = (periodicity: Periodicity.MAT | Periodicity.YTD, timePeriodOptions: TimePeriod) => {
  const rawMonthlySlice = timePeriodOptions.monthly;
  const rawWeeklySlice = timePeriodOptions.weekly;

  let keyword = '';

  switch (periodicity) {
    case Periodicity.MAT: {
      keyword = 'MAT';
      break;
    }
    case Periodicity.YTD: {
      keyword = 'YTD';
      break;
    }
  }

  const monthlySlice = rawMonthlySlice.filter((entry): boolean => entry.label.includes(keyword));
  const weeklySlice = rawWeeklySlice.filter((entry): boolean => entry.label.includes(keyword));

  return monthlySlice.concat(weeklySlice);
};

/**
 * TOBEREMOVE: To be removed once the implementation of calculation for MAT and YTD change in market performance card
 * Only applicable for Retailer Overview (so far?).
 * Returns a new period combo, if the current period combo should be reset.
 * When:
 * 1. Period combo does not need to be reset: returns null.
 * 2. Period combo periodicity is not MONTHLY/WEEKLY: returns the latest MONTHLY/WEEKLY period combo.
 * 3. For MONTHLY/WEEKLY, time period option is empty: returns the latest opposite periodicity period combo.
 * 4. Period is not latest : returns the latest period combo of that periodicity.
 * @param givenCombo the current period combo
 * @param timePeriodOptions all available period options, across all available periodicities
 * @returns newPeriodCombo if givenCombo should be reset
 * @returns null if givenCombo should be persisted
 */
export const getRetailerOverviewFallbackPeriodCombo = (
  givenCombo: PeriodCombo,
  timePeriodOptions: TimePeriod,
): PeriodCombo | null => {
  const resetPeriodicity = getRetailerOverviewFallbackPeriodicity(givenCombo, timePeriodOptions);

  const customMonthsDefaultCombo: CustomMonthsPeriod = getDefaultCustomMonthsPeriod(timePeriodOptions);

  const customWeeksDefaultCombo: CustomWeeksPeriod = getDefaultCustomWeeksPeriod(timePeriodOptions);

  if (resetPeriodicity === null) {
    return null;
  }

  return getLatestPeriodCombo(
    timePeriodOptions,
    resetPeriodicity,
    givenCombo.period,
    givenCombo.comparisonPeriod,
    customMonthsDefaultCombo,
    customWeeksDefaultCombo,
  );
};

/**
 * TOBEREMOVE: To be removed once the implementation of calculation for MAT and YTD change in market performance card
 * Only applicable for Retailer Overview (so far?).
 * Returns a periodicity to reset to, if the current period combo should be reset.
 * If the period combo does not need to be reset, returns null.
 * If the period combo needs to be reset, either returns the same periodicity, or a fallback monthly/weekly
 * periodicity depending on conditions.
 */
const getRetailerOverviewFallbackPeriodicity = (givenCombo: PeriodCombo, timePeriodOptions: TimePeriod) => {
  const { period: givenPeriod, periodicity: givenPeriodicity } = givenCombo;
  const supportedPeriodicities: Periodicity[] = [Periodicity.Monthly, Periodicity.Weekly];

  if (!supportedPeriodicities.includes(givenPeriodicity)) {
    return getMonthlyWeeklyPeriodicity(timePeriodOptions);
  }

  // from this point onwards, only Monthly/Weekly

  const periodicityKey = getPeriodicityKey(givenPeriodicity) as keyof TimePeriod;

  const oppositePeriodicity = givenPeriodicity === Periodicity.Monthly ? Periodicity.Weekly : Periodicity.Monthly;

  if (!timePeriodOptions[periodicityKey] || timePeriodOptions[periodicityKey].length === 0) {
    return oppositePeriodicity;
  }

  const periods = getPeriodsWithoutYtdMat(timePeriodOptions[periodicityKey]);
  const latestPeriod = periods[periods.length - 1];

  // if given period is latest, then should not be reset
  if (givenPeriod.label === latestPeriod.label) {
    return null;
  }

  return givenPeriodicity;
};
